import { createContext } from 'react';
import { contactsApis } from '@/configs/apis/contacts';
import { useSessionContext } from '@/contexts/session/context';
import { getAvatar } from '@/scripts/utils/genAvatar';
import { defaultMsgExpireDuration } from '@/configs/options';
import { useChatContext } from '../chat/context';
import {
  ChatMessageCategory,
  ChatMessageStatus,
  CustomChatMessagePayloadType,
} from '../chat/scripts/hooks/useChatMessages/types';
import { serverTime } from '../chat/scripts/hooks/useSystemTime/utils';
import type { Contact, CreateContactParams, PaginationParams } from './types';

export type ContactsContextValue = ReturnType<typeof useContactsContextValue>;

export const contactsContext = createContext<ContactsContextValue>({} as never);

export function useContactsContextValue() {
  const {
    session: { user },
  } = useSessionContext();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const {
    addMessage,
    chatsState: { activatedChatId },
  } = useChatContext();

  const queryContacts = useCallback(async (params: PaginationParams) => {
    const res = await contactsApis.queryContacts(params);
    setContacts(res);
  }, []);

  const addContacts = useCallback(
    async (contacts: CreateContactParams[]) => {
      const res = await contactsApis.addContacts(
        contacts.map((item) => ({ ...item, avatar: getAvatar(item.avatar, item.name) }))
      );
      queryContacts({ limit: 10000, offset: 0 });
      await Promise.all(
        contacts.map(async (item) =>
          addMessage({
            chat_id: item.chat_id,
            message: {
              category: ChatMessageCategory.RECEIVE,
              status: ChatMessageStatus.READ,
              payload_type: CustomChatMessagePayloadType.ADD_TO_CONTACTS,
              payload: JSON.stringify(item),
              expire_duration: defaultMsgExpireDuration,
              read_at: serverTime.time,
            },
            updateLastRead: activatedChatId === item.chat_id,
          })
        )
      );
      return res;
    },
    [activatedChatId, addMessage, queryContacts]
  );

  const queryContactById = useCallback((id: string) => {
    return contactsApis.queryContactById(id);
  }, []);

  const queryContactByUserId = useCallback((user_id: string) => {
    return contactsApis.queryContactByUserId(user_id);
  }, []);

  const deleteContacts = useCallback((ids: string) => {
    return contactsApis.deleteContacts(ids);
  }, []);

  const updateContacts = useCallback((items: Contact[]) => {
    return contactsApis.updateContacts(items);
  }, []);

  const value = useMemo(
    () => ({
      contacts,
      addContacts,
      queryContacts,
      queryContactById,
      queryContactByUserId,
      deleteContacts,
      updateContacts,
    }),
    [
      contacts,
      addContacts,
      deleteContacts,
      queryContacts,
      queryContactById,
      queryContactByUserId,
      updateContacts,
    ]
  );

  useLayoutEffect(() => {
    if (user?.userId) {
      queryContacts({ limit: -1, offset: 0 });
    }
  }, [queryContacts, user?.userId]);

  return value;
}

export function useContactsContext() {
  return useContext(contactsContext);
}
